import React, { useState, useEffect } from 'react';
import { useLegalNotices } from '../../hooks/useLegalNotices';

const LegalNoticesBanner = ({
  location = 'banner',
  audience = 'all',
  className = '',
  position = 'top' // 'top' or 'bottom'
}) => {
  const { notices, loading, error, dismissNotice, acknowledgeNotice, clickNotice } = useLegalNotices(
    location,
    audience,
    { autoTrackViews: true }
  );

  const [currentNoticeIndex, setCurrentNoticeIndex] = useState(0);

  useEffect(() => {
    if (notices.length > 1) {
      const interval = setInterval(() => {
        setCurrentNoticeIndex((prev) => (prev + 1) % notices.length);
      }, 8000);
      return () => clearInterval(interval);
    }
  }, [notices.length]);

  if (loading || error || notices.length === 0) return null;

  const currentNotice = notices[currentNoticeIndex];

  return (
    <>
      <style>{`
        .legal-notices-banner {
          position: fixed;
          left: 0;
          right: 0;
          z-index: 1040;
          animation: slideDown 0.3s ease-out;
        }
        .legal-notices-banner.top { top: 0; }
        .legal-notices-banner.bottom { bottom: 0; animation: slideUp 0.3s ease-out; }
        .legal-notice-item {
          background: #000000;
          color: white;
          min-height: 50px;
          padding: 0;
          margin: 0;
          border: none;
          box-shadow: 0 2px 10px rgba(0,0,0,0.1);
          position: relative;
        }
        .legal-notice-content {
          display: flex;
          align-items: center;
          padding: 12px 20px;
          gap: 15px;
          position: relative;
        }
        .legal-notice-icon {
          width: 24px;
          height: 24px;
          display: flex;
          align-items: center;
          justify-content: center;
        }
        .legal-notice-text {
          flex: 1;
          display: flex;
          flex-direction: column;
          gap: 2px;
        }
        .legal-notice-title {
          font-weight: 600;
          font-size: 0.9rem;
          margin: 0;
        }
        .legal-notice-message {
          font-size: 0.8rem;
          opacity: 0.9;
        }
        .legal-notice-action-btn {
          background: #3498db;
          color: white;
          border: none;
          padding: 6px 16px;
          border-radius: 4px;
          font-size: 0.8rem;
          cursor: pointer;
        }
        .legal-notice-dismiss {
          background: none;
          border: none;
          color: rgba(255, 255, 255, 0.7);
          font-size: 16px;
          cursor: pointer;
          padding: 4px;
        }
        .legal-notice-navigation {
          display: flex;
          gap: 6px;
          align-items: center;
        }
        .nav-dot {
          width: 8px;
          height: 8px;
          border-radius: 50%;
          background: rgba(255,255,255,0.4);
          border: none;
          cursor: pointer;
        }
        .nav-dot.active { background: rgba(255,255,255,0.8); }
        .legal-notice-progress {
          position: absolute;
          bottom: 0;
          left: 0;
          width: 100%;
          height: 2px;
          background: rgba(255,255,255,0.2);
        }
        .progress-bar {
          height: 100%;
          background: #3498db;
          width: 0;
          animation: progressFill linear forwards;
        }
        @keyframes slideDown {
          from { transform: translateY(-100%); opacity: 0; }
          to { transform: translateY(0); opacity: 1; }
        }
        @keyframes slideUp {
          from { transform: translateY(100%); opacity: 0; }
          to { transform: translateY(0); opacity: 1; }
        }
        @keyframes progressFill {
          from { width: 0%; }
          to { width: 100%; }
        }
      `}</style>

      <div className={`legal-notices-banner ${position} ${className}`}>
        <LegalNoticeItem
          notice={currentNotice}
          onDismiss={dismissNotice}
          onAcknowledge={acknowledgeNotice}
          onClick={clickNotice}
          showNavigation={notices.length > 1}
          currentIndex={currentNoticeIndex}
          totalNotices={notices.length}
          onNavigate={setCurrentNoticeIndex}
        />
      </div>
    </>
  );
};

const LegalNoticeItem = ({
  notice,
  onDismiss,
  onAcknowledge,
  onClick,
  showNavigation,
  currentIndex,
  totalNotices,
  onNavigate
}) => {
  const [isVisible, setIsVisible] = useState(true);

    useEffect(() => {
      const delay = 8000; 
      const timer = setTimeout(() => {
        handleDismiss();
      }, delay);

      return () => clearTimeout(timer);
    }, [notice._id]);


  const handleDismiss = () => {
    setIsVisible(false);
    setTimeout(() => {
      onDismiss(notice._id);
    }, 300);
  };

  const handleAction = () => {
    onClick(notice._id);
    const btn = notice.actionButton;
    if (!btn) return;
    switch (btn.action) {
      case 'link':
        window.open(btn.url, '_blank');
        break;
      case 'acknowledge':
        onAcknowledge(notice._id);
        break;
      case 'dismiss':
        handleDismiss();
        break;
      case 'modal':
        break;
      default:
        break;
    }
  };

  if (!isVisible) return null;

  const getNoticeTypeClass = () => {
    const typeMap = {
      info: 'notice-info',
      warning: 'notice-warning',
      error: 'notice-error',
      success: 'notice-success',
      maintenance: 'notice-maintenance',
      legal: 'notice-legal',
      privacy: 'notice-privacy'
    };
    return typeMap[notice.type] || 'notice-info';
  };

  const getNoticeIcon = () => {
    const iconMap = {
      info: 'fas fa-info-circle',
      warning: 'fas fa-exclamation-triangle',
      error: 'fas fa-times-circle',
      success: 'fas fa-check-circle',
      maintenance: 'fas fa-tools',
      legal: 'fas fa-gavel',
      privacy: 'fas fa-shield-alt'
    };
    return iconMap[notice.type] || 'fas fa-info-circle';
  };

  return (
    <>
      <link rel="stylesheet" href="https://cdnjs.cloudflare.com/ajax/libs/font-awesome/6.5.0/css/all.min.css" />
      <div
        className={`legal-notice-item ${getNoticeTypeClass()} ${notice.cssClass || ''}`}
        style={notice.customStyles || {}}
      >
        <div className="legal-notice-content">
          <div className="legal-notice-icon">
            <i className={getNoticeIcon()} />
          </div>
          <div className="legal-notice-text">
            <span className="legal-notice-title">{notice.title}</span>
            <span className="legal-notice-message" dangerouslySetInnerHTML={{ __html: notice.content }} />
          </div>
          {notice.actionButton?.text && (
            <button className="legal-notice-action-btn" onClick={handleAction}>
              {notice.actionButton.text}
            </button>
          )}
          {showNavigation && (
            <div className="legal-notice-navigation">
              {Array.from({ length: totalNotices }, (_, i) => (
                <button
                  key={i}
                  className={`nav-dot ${i === currentIndex ? 'active' : ''}`}
                  onClick={() => onNavigate(i)}
                />
              ))}
            </div>
          )}
          {notice.dismissible && (
            <button className="legal-notice-dismiss" onClick={handleDismiss} title="Dismiss">
              <i className="fas fa-times"></i>
            </button>
          )}
        </div>
        {/* <div className="legal-notice-progress">
          <div
            className="progress-bar"
            style={{ animationDuration: `${ 8000}ms` }}
          />
        </div> */}
      </div>
    </>
  );
};

export default LegalNoticesBanner;
